import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useAppContext } from '../context/AppContext';
import {
  getEntries, getBudget, getAssociates,
  getAllDepartmentAssociates, getDepartments,
  getShifts, saveEntry, deleteEntry, submitEntries, unsubmitEntry
} from '../services/api';
import EntryModal from '../components/EntryModal';
import LoadingPage from './LoadingPage';

const fmt = (n) => `$${Number(n).toLocaleString('en-US', { 
  minimumFractionDigits: 2, maximumFractionDigits: 2 
})}`;

const today   = () => new Date().toISOString().split('T')[0];
//const minDate = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; };
const maxDate = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; };


const formatDate = (d) => {
  if (!d) return null;
  const [year, month, day] = d.split('-');
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

function StatusBadge({ status }) {
  const map = {
    'Draft':     'badge-draft',
    'Submitted': 'badge-submitted',
    'Approved':  'badge-approved',
    'Rejected':  'badge-rejected',
  };
  return <span className={`badge ${map[status] || 'badge-draft'}`}>{status}</span>;
}

// ── Inline criteria change panel ──────────────────────────────────────────────
function CriteriaPanel({ selection, onApply }) {
  const [expanded,     setExpanded]     = useState(false);
  const [departments,  setDepartments]  = useState([]);
  const [shifts,       setShifts]       = useState([]);
  const [departmentId, setDepartmentId] = useState(selection.departmentId);
  const [shiftId,      setShiftId]      = useState(selection.shiftId);
  const [shiftDate,    setShiftDate]    = useState(selection.shiftDate);
  const [errors,       setErrors]       = useState({});
  const [loading,      setLoading]      = useState(false);

  const minDate = selection.minDate || '';

  
  // Load departments when panel opens
  useEffect(() => {
    if (!expanded) return;
    getDepartments(selection.facilityId)
      .then(res => setDepartments(res.data))
      .catch(() => {});
  }, [expanded, selection.facilityId]);

  // Cascade shifts when department changes
  useEffect(() => {
    if (!departmentId) { setShifts([]); setShiftId(''); return; }
    getShifts(departmentId)
      .then(res => setShifts(res.data))
      .catch(() => {});
  }, [departmentId]);

  const handleApply = () => {
    const errs = {};
    if (!departmentId) errs.department = 'Please select a department.';
    // Check shift is selected AND exists in current shifts list for selected department
  const shiftIsValid = shiftId && shifts.some(s => s.guidShiftID === shiftId);
  if (!shiftIsValid) errs.shift = 'Please select a shift.';
    if (!shiftDate)    errs.shiftDate  = 'Please select a date.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const department = departments.find(d => d.guidDepartmentID === departmentId);
    const shift      = shifts.find(s => s.guidShiftID === shiftId);

    onApply({ 
      ...selection, 
      department, 
      departmentId, 
      shift, 
      shiftId, 
      shiftDate 
    });
    setExpanded(false);
  };

  const handleCancel = () => {
    // Reset to current selection
    setDepartmentId(selection.departmentId);
    setShiftId(selection.shiftId);
    setShiftDate(selection.shiftDate);
    setErrors({});
    setExpanded(false);
  };

  return (
    <div style={{
      background:    'var(--white)',
      border:        '1px solid var(--gray-200)',
      borderRadius:  'var(--radius-lg)',
      marginBottom:  '1.25rem',
      overflow:      'hidden',
      boxShadow:     'var(--shadow-sm)'
    }}>
      {/* Summary row — always visible */}
      <div style={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
        padding:        '.85rem 1.25rem',
        background:     expanded ? 'var(--gray-50)' : 'var(--white)',
        borderBottom:   expanded ? '1px solid var(--gray-200)' : 'none'
      }}>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '.85rem' }}>
          <span style={{ color: 'var(--gray-400)', fontSize: '.75rem', 
                         textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Current Selection:
          </span>
          <CriteriaChip label="Facility"   value={selection.facility?.strName} />
          <CriteriaChip label="Department" value={selection.department?.strName} />
          <CriteriaChip label="Shift"      value={selection.shift?.strName} />
          <CriteriaChip label="Date"       value={formatDate(selection.shiftDate)} />
        </div>
        <button
          className="btn btn-outline btn-sm"
          onClick={() => setExpanded(!expanded)}
          style={{ whiteSpace: 'nowrap' }}
        >
          {expanded ? '✕ Cancel' : '✎ Change'}
        </button>
      </div>

      {/* Expandable criteria form */}
      {expanded && (
        <div style={{
          padding:    '1.25rem',
          background: 'var(--gray-50)',
          display:    'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap:        '1rem',
          alignItems: 'end'
        }}>
          {/* Department */}
          <div className="form-group">
            <label className="form-label">Department</label>
            <select
              className={`form-control${errors.department ? ' error' : ''}`}
              value={departmentId}
              onChange={e => setDepartmentId(e.target.value)}
            >
              <option value="">— Select —</option>
              {departments.map(d => (
                <option key={d.guidDepartmentID} value={d.guidDepartmentID}>
                  {d.strName}
                </option>
              ))}
            </select>
            {errors.department && <span className="form-error">{errors.department}</span>}
          </div>

          {/* Shift */}
          <div className="form-group">
            <label className="form-label">Shift</label>
            <select
              className={`form-control${errors.shift ? ' error' : ''}`}
              value={shiftId}
              onChange={e => setShiftId(e.target.value)}
              disabled={!departmentId}
            >
              <option value="">— Select —</option>
              {shifts.map(s => (
                <option key={s.guidShiftID} value={s.guidShiftID}>
                  {s.strName}
                </option>
              ))}
            </select>
            {errors.shift && <span className="form-error">{errors.shift}</span>}
          </div>

          {/* Date */}
          <div className="form-group">
            <label className="form-label">Shift Date</label>
            <input
              type="date"
              className={`form-control${errors.shiftDate ? ' error' : ''}`}
              value={shiftDate}
              min={selection.minDate}
              max={maxDate()}
              onChange={e => setShiftDate(e.target.value)}
            />
            {selection.payPeriodStart && (
            <span style={{ fontSize: '.72rem', color: 'var(--gray-400)' }}>
              Dates before <strong>{formatDate(selection.payPeriodStart)}</strong> are historical and read-only.
            </span>
            )}
            {errors.shiftDate && <span className="form-error">{errors.shiftDate}</span>}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '.6rem' }}>
            <button
                className="btn btn-gold"
                onClick={handleApply}
                disabled={
                  !departmentId || 
                  !shiftDate ||
                  !shifts.some(s => s.guidShiftID === shiftId)  // ← shift must exist in current list
                }
                style={{ flex: 1, justifyContent: 'center' }}
              >
                Apply
              </button>
            <button
              className="btn btn-outline"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CriteriaChip({ label, value }) {
  if (!value) return null;
  return (
    <span style={{ color: 'var(--gray-800)' }}>
      <span style={{ color: 'var(--gray-400)', marginRight: '.3rem' }}>{label}:</span>
      <strong>{value}</strong>
    </span>
  );
}

// ── Main Lead Pay Page ────────────────────────────────────────────────────────
export default function LeadPayPage() {
 const { signOut } = useAuth();
  const { user, selection, setSelection } = useAppContext();
  const navigate            = useNavigate();

  const [entries,       setEntries]       = useState([]);
  const [budget,        setBudget]        = useState(0);
  const [associates,    setAssociates]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [modal,         setModal]         = useState(null);
  const [alert,         setAlert]         = useState(null);
  const [submitting,    setSubmitting]    = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [associateOverride, setAssociateOverride] = useState(false);

  useEffect(() => {
    if (!selection) navigate('/');
  }, [selection, navigate]);

  const loadData = useCallback(async () => {
    if (!selection) return;
    setLoading(true);
    try {
      const [entriesRes, budgetRes] = await Promise.all([
        getEntries(selection.departmentId, selection.shiftId, selection.shiftDate),
        getBudget(selection.departmentId,  selection.shiftId, selection.shiftDate),
      ]);
      setEntries(entriesRes.data);
      setBudget(budgetRes.data.amount || 0);
    } catch {
      setAlert({ type: 'error', message: 'Failed to load lead pay data. Please refresh.' });
    } finally {
      setLoading(false);
    }
  }, [selection]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Handle criteria change from inline panel ──────────────────────────────
  const handleCriteriaChange = useCallback((newSelection) => {
    setSelection(newSelection);
    setAlert({ 
      type: 'success', 
      message: `Criteria updated to ${newSelection.department?.strName} — ${newSelection.shift?.strName} on ${formatDate(newSelection.shiftDate)}.` 
    });
  }, [setSelection]);

  const grandTotal   = entries.reduce((sum, e) => sum + (e.dblPayAmount || 0), 0);
  // Fixed — blocks submission when budget is 0 or not set:
const noBudget    = budget === 0;
const isOverBudget = noBudget ? grandTotal > 0 : grandTotal > budget;
  const hasUnsubmitted = entries.some(e => e.intStatus !== 2);
const isHistorical = selection.shiftDate < selection.payPeriodStart;
console.log('shiftDate:', selection.shiftDate, typeof selection.shiftDate);
console.log('payPeriodStart:', selection.payPeriodStart, typeof selection.payPeriodStart);
console.log('isHistorical:', isHistorical);
  const handleNewClick = async (useOverride = false) => {
    try {
      const fetchAssociates = useOverride
        ? getAllDepartmentAssociates
        : getAssociates;

      const res = await fetchAssociates(
        selection.departmentId,
        selection.shiftId,
        selection.shiftDate
      );
      setAssociates(res.data);
      setAssociateOverride(useOverride);
      setModal({ mode: 'new' });
    } catch {
      setAlert({ type: 'error', message: 'Failed to load associates.' });
    }
  };

  const handleEditClick = (entry) => {
    if (entry.intStatus === 2) {
      setAlert({ type: 'warning', message: 'Submitted entries cannot be edited.' });
      return;
    }
    setModal({ mode: 'edit', entry });
  };

  const handleUnsubmitAndEdit = async (entry) => {
  const confirmed = window.confirm(
    `This entry has already been submitted.\n\nAre you sure you want to unsubmit this entry and make changes to it?\n\nAssociate: ${entry.leadName}`
  );
  if (!confirmed) return;

  try {
    await unsubmitEntry(entry.guidLeadPayHeaderID);
    await loadData();  // refresh grid to show updated status
    // Now open the edit modal with the refreshed entry
    setModal({ mode: 'edit', entry: { ...entry, intStatus: 1, status: 'Draft' } });
    setAlert({ type: 'success', message: `${entry.leadName} has been unsubmitted and is ready to edit.` });
  } catch {
    setAlert({ type: 'error', message: 'Failed to unsubmit entry. Please try again.' });
  }
};

  const handleSave = async ({ guidAssociateID, dblPayAmount, strComments }) => {
    setSaving(true);
    try {
      await saveEntry({
        guidLeadPayHeaderID: modal.mode === 'edit' ? modal.entry.guidLeadPayHeaderID : null,
        guidAssociateID:     modal.mode === 'edit' ? modal.entry.guidAssociateID : guidAssociateID,
        guidDepartmentID:    selection.departmentId,
        guidShiftID:         selection.shiftId,
        dteShiftDate:        selection.shiftDate,
        dblPayAmount,
        strComments,
        updatedBy:           user?.associateId || '',
      });
      setModal(null);
      setAlert({ type: 'success', message: 'Entry saved successfully.' });
      await loadData();
    } catch {
      setAlert({ type: 'error', message: 'Failed to save entry. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (headerId) => {
    setSaving(true);
    try {
      await deleteEntry(headerId);
      setModal(null);
      setAlert({ type: 'success', message: 'Entry deleted.' });
      await loadData();
    } catch {
      setAlert({ type: 'error', message: 'Failed to delete entry.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (noBudget && grandTotal > 0) {
    setAlert({ 
      type: 'error', 
      message: 'Cannot submit: no budget has been configured for this department, shift and day. Please contact your administrator.' 
    });
    setConfirmSubmit(false);
    return;
  }

  if (isOverBudget) {
    setAlert({ 
      type: 'error', 
      message: `Cannot submit: total pay amount ${fmt(grandTotal)} exceeds the budget of ${fmt(budget)}.` 
    });
    setConfirmSubmit(false);
    return;
  }
    if (!confirmSubmit) { setConfirmSubmit(true); return; }

    setSubmitting(true);
    setConfirmSubmit(false);
    try {
      const unsubmitted = entries
        .filter(e => e.intStatus !== 2)
        .map(e => e.guidLeadPayHeaderID);

      if (!unsubmitted.length) {
        setAlert({ type: 'warning', message: 'All entries are already submitted.' });
        return;
      }
      await submitEntries({ headerIds: unsubmitted, submittedBy: user?.associateId || '' });
      setAlert({ type: 'success', message: `Successfully submitted ${unsubmitted.length} entry(s) to Corporate.` });
      await loadData();
    } catch {
      setAlert({ type: 'error', message: 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!selection) return null;
  if (loading)    return <LoadingPage message="Loading lead pay data…" />;

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      {/* Historical data banner */}
      {isHistorical && (
        <div style={{
          background:   'var(--amber-light)',
          border:       '1px solid var(--amber)',
          borderLeft:   '4px solid var(--amber)',
          borderRadius: 'var(--radius)',
          padding:      '.85rem 1.25rem',
          marginBottom: '1.25rem',
          display:      'flex',
          alignItems:   'center',
          gap:          '.75rem',
          fontSize:     '.875rem',
          color:        'var(--amber)'
        }}>
          <span style={{ fontSize: '1.25rem' }}>📋</span>
          <div>
            <strong>Historical View</strong> — You are viewing data from a closed pay period. 
            Entries are read-only and cannot be modified or submitted.
          </div>
        </div>
      )}
      {/* Alert banner */}
      {alert && (
        <div className={`alert alert-${alert.type}`} 
             style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{alert.message}</span>
          <button
            onClick={() => setAlert(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', 
                     fontSize: '1.1rem', color: 'inherit' }}
          >×</button>
        </div>
      )}

      {/* ── Inline criteria change panel ── */}
      <CriteriaPanel 
        selection={selection} 
        onApply={handleCriteriaChange} 
      />

      {/* Budget bar */}
      <div className="budget-bar">
  <div className="budget-bar__item">
    <div className="budget-bar__label">Pay Total</div>
    <div className={`budget-bar__value${isOverBudget ? ' over' : ''}`}>
      {fmt(grandTotal)}
    </div>
    {isOverBudget && !noBudget && (
      <div style={{ fontSize: '.72rem', color: '#ff8a80', marginTop: '.2rem' }}>
        ⚠ Exceeds budget by {fmt(grandTotal - budget)}
      </div>
    )}
    {noBudget && grandTotal > 0 && (
      <div style={{ fontSize: '.72rem', color: '#ff8a80', marginTop: '.2rem' }}>
        ⚠ No budget configured for this selection
      </div>
    )}
  </div>
  <div className="budget-bar__divider" />
  <div className="budget-bar__item">
    <div className="budget-bar__label">Budget</div>
    <div className={`budget-bar__value${noBudget ? ' over' : ''}`}>
      {budget > 0 ? fmt(budget) : '$0.00 — Not Set'}
    </div>
  </div>
  <div className="budget-bar__divider" />
  <div className="budget-bar__item">
    <div className="budget-bar__label">Remaining</div>
    <div className={`budget-bar__value${isOverBudget || noBudget ? ' over' : ''}`}>
      {budget > 0 ? fmt(budget - grandTotal) : fmt(0 - grandTotal)}
    </div>
  </div>
</div>

      {/* Main card */}
      <div className="card">
        <div className="card-header">
          <h2 style={{ fontSize: '1.1rem' }}>
            Lead Pay Entries
            <span style={{
              marginLeft: '.75rem', fontSize: '.8rem', fontFamily: 'DM Sans',
              background: 'var(--gray-100)', color: 'var(--gray-600)',
              padding: '.2rem .6rem', borderRadius: 20, fontWeight: 500
            }}>
              {entries.length} record{entries.length !== 1 ? 's' : ''}
            </span>
          </h2>
          <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center' }}>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/')}>
              ← Back
            </button>
            {/* Only show entry buttons for current pay period */}
  {!isHistorical && (
    <>
      <button className="btn btn-primary btn-sm" onClick={() => handleNewClick(false)}>
        + New Entry
      </button>
      <button
        className="btn btn-outline btn-sm"
        onClick={() => handleNewClick(true)}
        style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}
      >
        + New Entry (Override)
      </button>
    </>
  )}
            {!isHistorical && hasUnsubmitted && (
  <button
    className={`btn btn-sm ${confirmSubmit ? 'btn-danger' : 'btn-success'}`}
    onClick={handleSubmit}
    disabled={submitting || isOverBudget || (noBudget && grandTotal > 0)}
    title={
      noBudget && grandTotal > 0
        ? 'Cannot submit: no budget configured'
        : isOverBudget
        ? 'Cannot submit: pay total exceeds budget'
        : ''
    }
  >
    {submitting ? 'Submitting…' : confirmSubmit ? '⚠ Confirm Submit' : '✈ Submit to Corporate'}
  </button>
)}
            {confirmSubmit && (
              <button className="btn btn-outline btn-sm" onClick={() => setConfirmSubmit(false)}>
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Grid */}
        {entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '.75rem' }}>📋</div>
            <p style={{ fontSize: '.9rem' }}>
              No entries yet. Click <strong>+ New Entry</strong> to get started.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lead Name</th>
                  <th className="col-amount">Pay Amount</th>
                  <th>Comments</th>
                  <th>Status</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.guidLeadPayHeaderID}>
                    <td style={{ fontWeight: 500 }}>{entry.leadName}</td>
                    <td className="col-amount">{fmt(entry.dblPayAmount)}</td>
                    <td style={{ color: 'var(--gray-600)', fontSize: '.85rem' }}>
                      {entry.strComments || <span style={{ color: 'var(--gray-400)' }}>—</span>}
                    </td>
                    <td><StatusBadge status={entry.status} /></td>
                    <td>
  <button
    className="btn btn-outline btn-sm"
    onClick={() => {
      if (isHistorical) {
        // Historical — open in read-only view mode
        setModal({ mode: 'edit', entry });
      } else if (entry.intStatus === 2) {
        handleUnsubmitAndEdit(entry);
      } else {
        handleEditClick(entry);
      }
    }}
  >
    {isHistorical ? '👁 View' : entry.intStatus === 2 ? '↩ Edit' : '✏ Edit'}
  </button>
</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 700, color: 'var(--navy)' }}>Total</td>
                  <td className="col-amount"
                    style={{ color: isOverBudget ? 'var(--red)' : 'var(--green)', fontWeight: 700 }}>
                    {fmt(grandTotal)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Entry Modal */}
      {modal && (
        <EntryModal
          mode={modal.mode}
          entry={modal.entry}
          associates={associates}
          isOverride={associateOverride}
          isHistorical={isHistorical}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => { setModal(null); setConfirmSubmit(false); }}
          saving={saving}
        />
      )}
    </div>
  );
}